<script setup>
// core
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {useStore} from "vuex";

// data
import MESSENGERS, {TIKTOK} from "@/data/messengers.js";

// assets
import viberTelephonySvg from "@/assets/icons/messengers/viber_telephony.svg";

const AUTOPLAY_DELAY = 7000;
const VIBER_DOCS = "https://e-chat.tech/api/viber/v2/documentation";
const EARLY_ACCESS_SLIDES = ["telephony", "tiktok"];

const store = useStore();
const router = useRouter();

const current = ref(0);
let timer = null;

const close = () => store.commit("closeDialog");

const slides = computed(() => [
    {
        key: "telephony",
        color: "var(--general-color)",
        tint: "#f6f5fd",
        icon: viberTelephonySvg,
        buttonIcon: "bi bi-telephone-plus",
        noteIcon: "bi bi-gift",
        icons: ["bi bi-telephone-inbound", "bi bi-people", "bi bi-record-circle"],
        action: () => {
            close();
            router.push({name: "TelephonyActivation"});
        },
        link: () => {
            close();
            router.push({name: "TelephonySettings"});
        },
    },
    {
        key: "tiktok",
        color: TIKTOK.color,
        tint: "#ededed",
        icon: TIKTOK.svg,
        buttonIcon: "bi bi-person-plus",
        noteIcon: "bi bi-gift",
        icons: ["bi bi-chat-dots", "bi bi-reply", "bi bi-broadcast"],
        action: () => {
            close();
            router.push({name: "TiktokActivation"});
        },
    },
    {
        key: "viber",
        color: MESSENGERS.viber.color,
        tint: "#f6f3f8",
        icon: MESSENGERS.viber.svg,
        buttonIcon: "bi bi-box-arrow-up-right",
        noteIcon: "bi bi-shield-check",
        icons: ["bi bi-pencil-square", "bi bi-chat-quote", "bi bi-clock-history"],
        action: () => window.open(VIBER_DOCS, "_blank", "noopener"),
    },
// !TODO: the Viber API slide is hidden for now, drop this filter to bring it back
].filter((slide) => slide.key !== "viber")
    // telephony and TikTok are open to the early access accounts only
    .filter((slide) => store.getters["auth/hasEarlyAccess"] || !EARLY_ACCESS_SLIDES.includes(slide.key)));

const activeSlide = computed(() => slides.value[current.value]);

const show = (index) => {
    current.value = (index + slides.value.length) % slides.value.length;
};

const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
};

const play = () => {
    stop();
    timer = setInterval(() => show(current.value + 1), AUTOPLAY_DELAY);
};

const onNav = (index) => {
    show(index);
    play();
};

onMounted(play);
onBeforeUnmount(stop);
</script>

<template>
    <slot name="header_close"/>

    <div class="wrapper_dialog announce" @mouseenter="stop" @mouseleave="play">
        <div class="announce__viewport">
            <div class="announce__track" :style="{ transform: `translateX(-${current * 100}%)` }">
                <section
                    v-for="slide in slides"
                    :key="slide.key"
                    class="announce__slide"
                    :style="{ '--svc': slide.color, '--tint': slide.tint }"
                >
                    <header class="announce__hero">
                        <span class="announce__icon">
                            <img :src="slide.icon" alt=""/>
                        </span>

                        <div class="announce__headings">
                            <span
                                v-if="$t(`dialog.announcement.${slide.key}.ribbon`)"
                                class="announce__flag"
                            >{{ $t(`dialog.announcement.${slide.key}.ribbon`) }}</span>

                            <h3 class="announce__title">
                                {{ $t(`dialog.announcement.${slide.key}.title`) }}
                            </h3>
                            <p class="announce__lead">
                                {{ $t(`dialog.announcement.${slide.key}.lead`) }}
                            </p>
                        </div>
                    </header>

                    <div class="announce__features">
                        <div v-for="(icon, i) in slide.icons" :key="icon" class="announce__feature">
                            <span class="announce__feature-icon"><i :class="icon"></i></span>
                            <p class="announce__feature-title">
                                {{ $t(`dialog.announcement.${slide.key}.f${i + 1}_title`) }}
                            </p>
                            <p class="announce__feature-text">
                                {{ $t(`dialog.announcement.${slide.key}.f${i + 1}_text`) }}
                            </p>
                        </div>
                    </div>

                    <div class="announce__note">
                        <div class="announce__note-head">
                            <span class="announce__feature-icon"><i :class="slide.noteIcon"></i></span>
                            <b>{{ $t(`dialog.announcement.${slide.key}.note_title`) }}</b>
                        </div>
                        <p>{{ $t(`dialog.announcement.${slide.key}.note`) }}</p>
                    </div>

                    <div class="announce__actions">
                        <button type="button" class="announce__button" @click="slide.action">
                            <i :class="slide.buttonIcon"></i>
                            {{ $t(`dialog.announcement.${slide.key}.button`) }}
                        </button>

                        <a v-if="slide.link" class="announce__link" @click="slide.link">
                            {{ $t("dialog.announcement.link") }}
                        </a>
                    </div>
                </section>
            </div>
        </div>

        <div class="announce__nav">
            <button type="button" class="announce__arrow" @click="onNav(current - 1)">
                <i class="bi bi-chevron-left"></i>
            </button>

            <div class="announce__dots" :key="current">
                <button
                    v-for="(slide, i) in slides"
                    :key="slide.key"
                    type="button"
                    class="announce__dot"
                    :class="{ 'is-current': i === current }"
                    @click="onNav(i)"
                >
                    <span class="announce__dot-fill"></span>
                </button>
            </div>

            <button type="button" class="announce__arrow" @click="onNav(current + 1)">
                <i class="bi bi-chevron-right"></i>
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use "@/styles/announcement.scss";
</style>
